import SEO, { type Crumb } from '@/components/SEO';
import { Link } from 'react-router-dom';
import ServiceExperience from '@/components/services/ServiceExperience';
import ContentIndex from '@/components/ContentIndex';
import CTABand from '@/components/CTABand';
import { contentBySection, staticPage } from '@/lib/content';

const MOVES = [
  {
    title: 'Repository to OCI',
    body: 'Move content, metadata and permissions with the database, file store and integrations. For OCI hosting, assess cluster design and validate session continuity, Coherence caching and failover behavior.',
    href: '/migrations/webcenter-content-repository-to-oci',
    link: 'Explore the repository move',
  },
  {
    title: 'AP workflows and ERP changes',
    body: 'Plan a new EBS instance or a PeopleSoft, JD Edwards or Fusion Cloud connection. Agree workflow closeout or in-flight handling, then reconcile invoice status and document access at cutover.',
    href: '/migrations/in-flight-invoice-migration',
    link: 'Plan the invoice cutover',
  },
  {
    title: 'Invoice recognition and exception handling',
    body: 'Review WebCenter Forms Recognition (WFR) rules, validation and recurring exceptions against real invoices. Compare extraction options with the coding, approval and handoff work they need to support.',
    href: '/migrations/wfr-vs-oci-document-understanding',
    link: 'Compare the extraction approaches',
  },
  {
    title: 'Content and application redesign',
    body: 'Plan retention, managed attachments and custom forms around the work they support. Refactor SOA workflows, non-PO account distributions and approvals where the new process requires it.',
    href: '/migrations/modernize-oracle-webcenter',
    link: 'Explore the modernization decisions',
  },
];

const Migrations = () => {
  const page = staticPage('/migrations');
  const items = contentBySection('migrations');
  const crumbs: Crumb[] = [
    { name: 'Home', href: '/' },
    { name: 'Migrations', href: '/migrations' },
  ];
  return (
    <>
      <SEO title={page.title} description={page.description} path="/migrations" breadcrumbs={crumbs} />
      <ServiceExperience
        kind="migrations"
        crumbs={crumbs}
        label="Migrations"
        title="Move the workload. Keep the estate."
        lead="Decide what stays, what moves and what is rebuilt. We plan content migration, workflow continuity and ERP connections around the work that must carry through cutover."
        note="OCI, Fusion Cloud and 14c-era architectures. The right destination depends on what the workload has to do."
      />

      <section aria-labelledby="moves-heading" className="container service-section">
        <div className="service-section-heading">
          <div><p className="label">THE WORKLOADS</p><h2 id="moves-heading">Four moves.<br />Different constraints.</h2></div>
          <p>Define the content, transactions and integrations in scope. Agree which workflows finish before the move, how any remaining work is handled and what must be reconciled at cutover.</p>
        </div>
        <div className="service-moves">
          {MOVES.map((m, i) => (
            <article key={m.title} className="service-move">
              <span aria-hidden="true" className="service-move-number">0{i + 1}</span>
              <h3>{m.title}</h3>
              <div><p>{m.body}</p><Link to={m.href} className="service-text-link">{m.link} <span aria-hidden="true">↗</span></Link></div>
            </article>
          ))}
        </div>
        <aside className="service-decision-note">
          <h3>Plan the platform and the process together.</h3>
          <div><p>An OCI deployment needs resilience tests. The invoice process needs its own checks for recognition, coding, approvals and document access. Confirm each component and integration against the target environment.</p><Link to="/migrations/webcenter-oci-marketplace-vs-modernize" className="service-text-link mt-4">Marketplace or modernization? <span aria-hidden="true">↗</span></Link></div>
        </aside>
      </section>

      <section aria-labelledby="guides-heading" className="container pb-14 md:pb-20">
        <div className="service-guides-heading">
          <h2 id="guides-heading">Follow the dependencies.</h2>
          <p>Migration guides that make the decisions, constraints and practical work explicit.</p>
        </div>
        <div>
          <ContentIndex
            items={items}
            emptyHeading="Migration guides are being added."
            emptyBody="WebCenter AP → Fusion, Imaging after 12c, repository → OCI, WFR vs OCI Document Understanding, Marketplace vs modernize, records retention and the managed-attachments and ADF form decisions are being written now."
          />
        </div>
      </section>

      <CTABand variant="engineer" />
    </>
  );
};

export default Migrations;
